import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class AuthService {
  currentUserName$!: BehaviorSubject<string>;

  private sessionWatchdogInterval?: ReturnType<typeof setInterval>;
  /** logout() triggers a full-page redirect; the flag makes concurrent 401/403
   *  responses (a burst of in-flight requests all failing at once) collapse to
   *  a single redirect instead of each re-firing it. */
  private loggingOut = false;

  constructor(private router: Router) {
    this.currentUserName$ = new BehaviorSubject<string>(sessionStorage.getItem('current-username') ?? '');
    if (this.currentUserName$.value && !this.isBackendTokenExpired()) {
      this.startSessionWatchdog();
    }
  }

  bootstrapFromFragment(): void {
    const hash = window.location.hash;
    if (hash.startsWith('#session=')) {
      const encoded = hash.substring(9);
      try {
        const payload = JSON.parse(atob(encoded)) as Record<string, string>;
        Object.entries(payload).forEach(([k, v]) => sessionStorage.setItem(k, v));
        // Strip only the #session= fragment (it's sensitive and shouldn't
        // linger in the address bar/history) — the query string is preserved
        // so a hand-off like Agent Hub's ?abilities= deep link survives for
        // the router's initial navigation to read.
        history.replaceState(null, '', window.location.pathname + window.location.search);
        this.currentUserName$.next(sessionStorage.getItem('current-username') ?? '');
        this.startSessionWatchdog();
      } catch {
        // Malformed fragment — ignore
      }
    }
  }

  get isAdmin(): boolean {
    // super_admin ⊇ admin — both get admin-level Studio affordances.
    const role = (sessionStorage.getItem('current-role') ?? '').toLowerCase();
    return role === 'admin' || role === 'super_admin';
  }

  isBackendTokenExpired(): boolean {
    try {
      const idToken = sessionStorage.getItem('id-token');
      if (!idToken) return true;
      const payload = JSON.parse(atob(idToken.split('.')[1])) as { exp?: number };
      if (!payload.exp) return true;
      return payload.exp < Math.floor(Date.now() / 1000);
    } catch {
      return true;
    }
  }

  isTokenValid(): boolean {
    return !this.isBackendTokenExpired();
  }

  logout(): void {
    if (this.loggingOut) return;  // a burst of 401s collapses to one redirect
    this.loggingOut = true;
    this.stopSessionWatchdog();
    sessionStorage.clear();
    this.currentUserName$.next('');
    // Land on the portal's login page — the session here is gone, and the
    // portal home would just bounce an unauthenticated visitor anyway. The
    // ?logout=true signal tells the portal to clear its own MSAL cache and
    // NOT silently re-authenticate (otherwise SSO bounces straight back in).
    window.location.href = `${environment.PORTAL_LOGIN_URL}?logout=true`;
  }

  startSessionWatchdog(): void {
    this.stopSessionWatchdog();
    this.sessionWatchdogInterval = setInterval(() => {
      if (this.isBackendTokenExpired()) {
        // Stop the watchdog FIRST — otherwise it re-clears storage and
        // re-navigates every 60s for the life of the tab after expiry.
        this.stopSessionWatchdog();
        sessionStorage.clear();
        this.currentUserName$.next('');
        this.router.navigate(['/unauthorized']);
      }
    }, 60_000);
  }

  private stopSessionWatchdog(): void {
    if (this.sessionWatchdogInterval !== undefined) {
      clearInterval(this.sessionWatchdogInterval);
      this.sessionWatchdogInterval = undefined;
    }
  }
}
